import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Network } from "lucide-react";

export default function Bracket() {
  const [matches, setMatches] = useState([]);

  useEffect(() => { api.get("/matches").then(({ data }) => setMatches(data)); }, []);

  const qf = matches.filter((m) => m.stage === "quarter");
  const sf = matches.filter((m) => m.stage === "semi");
  const fn = matches.filter((m) => m.stage === "final");

  // Pad each stage to expected size for clean visualization
  const padded = (arr, n) => {
    const out = [...arr];
    while (out.length < n) out.push(null);
    return out;
  };
  const qf8 = padded(qf, 4);
  const sf4 = padded(sf, 2);
  const fn1 = padded(fn, 1);

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Knockout Visualization</div>
        <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white">Bracket</h1>
        <div className="kenya-stripe-thin mt-2 w-32" />
      </div>

      {(qf.length + sf.length + fn.length) === 0 ? (
        <Card className="p-12 text-center bg-[#11241b] border-white/10" data-testid="bracket-empty">
          <Network className="w-12 h-12 mx-auto text-zinc-700 mb-4" />
          <div className="font-anton text-2xl uppercase text-white">No knockout bracket yet</div>
          <div className="text-sm text-zinc-400 mt-1">Generate a knockout from <span className="text-[#00A86B]">Schedule</span> once group matches are finished.</div>
        </Card>
      ) : (
        <Card className="p-6 bg-[#11241b] border-white/10 overflow-x-auto" data-testid="bracket-canvas">
          <div className="flex gap-12 items-stretch min-w-[820px]">
            <BracketColumn title="Quarter-Finals" matches={qf8} />
            <Connectors count={4} />
            <BracketColumn title="Semi-Finals" matches={sf4} />
            <Connectors count={2} />
            <BracketColumn title="Final" matches={fn1} accent />
          </div>
        </Card>
      )}
    </div>
  );
}

function BracketColumn({ title, matches, accent }) {
  return (
    <div className="flex flex-col justify-around min-w-[200px] gap-6">
      <div className={`text-[10px] uppercase tracking-[0.3em] text-center mb-1 ${accent ? "text-[#00A86B]" : "text-zinc-500"}`}>{title}</div>
      {matches.map((m, i) => (
        <div key={i} className={`rounded-md border p-3 ${m ? (accent ? "border-[#00A86B]/40 bg-[#00A86B]/5" : "border-white/10 bg-[#060d09]") : "border-dashed border-white/10 bg-[#060d09]/50"}`} data-testid={`bracket-cell-${i}`}>
          {m ? (
            <>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{m.court}</div>
              <TeamRow name={m.team_a?.name} placeholder={m.team_a_id === ""} winner={m.winner === "a"} score={m.score_a} />
              <TeamRow name={m.team_b?.name} placeholder={m.team_b_id === ""} winner={m.winner === "b"} score={m.score_b} />
              <div className="text-[10px] mt-1 text-zinc-500">{m.status}</div>
            </>
          ) : (
            <div className="text-xs text-zinc-600 text-center py-2">TBD</div>
          )}
        </div>
      ))}
    </div>
  );
}

function TeamRow({ name, placeholder, winner, score }) {
  return (
    <div className={`flex items-center justify-between text-sm ${winner ? "text-[#00A86B] font-bold" : placeholder ? "text-zinc-500 italic" : "text-white"}`}>
      <span className="truncate">{placeholder ? "Winner of previous" : (name || "—")}</span>
      <span className="font-mono-score">{score ?? 0}</span>
    </div>
  );
}

function Connectors({ count }) {
  // Vertical SVG that draws connector lines from `count` pairs into `count/2` outputs
  return (
    <div className="flex flex-col justify-around" style={{ width: 40 }}>
      <svg viewBox="0 0 40 600" preserveAspectRatio="none" className="w-full h-full">
        {Array.from({ length: count / 2 }).map((_, i) => {
          const segmentHeight = 600 / (count / 2);
          const yMid = segmentHeight * (i + 0.5);
          const yTop = segmentHeight * i + segmentHeight * 0.18;
          const yBot = segmentHeight * (i + 1) - segmentHeight * 0.18;
          return (
            <g key={i}>
              <line x1="0" y1={yTop} x2="20" y2={yTop} stroke="#00A86B" strokeWidth="2" />
              <line x1="0" y1={yBot} x2="20" y2={yBot} stroke="#00A86B" strokeWidth="2" />
              <line x1="20" y1={yTop} x2="20" y2={yBot} stroke="#00A86B" strokeWidth="2" />
              <line x1="20" y1={yMid} x2="40" y2={yMid} stroke="#00A86B" strokeWidth="2" />
            </g>
          );
        })}
      </svg>
    </div>
  );
}
